//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import isMatch from 'lodash/fp/isMatch';
import React, { useState } from 'react';
import styled from 'styled-components';
import { NavTab } from 'unison';

import { RouteAnchor } from '@components/RouteAnchor';
import Popover from '@components/molecules/popover/Popover';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { NavigateStateParams } from '@react-bridge/wrappers/navigation';
import { convertAllFieldsToString } from '@utils/stringUtils';
import { withTracking } from '@utils/withTracking';

import { RoutableStateName } from 'app/top-level-states';

interface Props<T extends RoutableStateName> {
  state: RoutableStateName;
  stateParams?: NavigateStateParams<T>;
  text: string;
  onClick?: (isActive: boolean) => void;
  stateMatcher?: (
    currentState: RoutableStateName,
    state: RoutableStateName,
  ) => boolean;
  stateParamsMatcher?: (
    currentParams?: NavigateStateParams<T>,
    stateParams?: NavigateStateParams<T>,
  ) => boolean;
  linkBadge?: React.ReactElement;
  linkIcon?: React.ReactElement;
  linkAction?: React.ReactNode;
  isNested?: boolean;
  isDisabled?: boolean;
  trackingId?: string;
}

const defaultStateMatcher = (
  currentState: RoutableStateName,
  state: RoutableStateName,
) => {
  return currentState.startsWith(state);
};

export const SidebarPageItem = <T extends RoutableStateName>({
  isDisabled = false,
  isNested = false,
  linkAction,
  linkBadge,
  linkIcon,
  onClick,
  state,
  stateMatcher = defaultStateMatcher,
  stateParams,
  stateParamsMatcher,
  text,
  trackingId,
}: Props<T>) => {
  const { params: currentParams, state: currentState } =
    useCurrentStateAndParams<NavigateStateParams<T>>();
  const [isHovered, setIsHovered] = useState(false);

  // For now, disable the popover for all sidebar page items. We can enable
  // it in the future using this conditional, but our original attempt to gate
  // it based on text length was met with negative feedback. A more elegant solution
  // would be to only show the popover if the text is ellipsized, but implementing
  // that logic requires querying the DOM, which isn't free.
  const disablePopover = true;

  const defaultStateParamsMatcher = (
    currentParams?: NavigateStateParams<T>,
    stateParams?: NavigateStateParams<T>,
  ) =>
    stateParams && currentParams
      ? isMatch(
          convertAllFieldsToString(stateParams) as object,
          convertAllFieldsToString(currentParams),
        )
      : true;

  const stateMatches = stateMatcher(currentState, state);
  const stateParamsMatch = (stateParamsMatcher ?? defaultStateParamsMatcher)(
    currentParams,
    stateParams,
  );
  const isActive = stateMatches && stateParamsMatch;

  return (
    <Container
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      trackingId={trackingId}
    >
      <Popover
        content={<PageItemPopover>{text}</PageItemPopover>}
        interactive={false}
        isDisabled={disablePopover}
        placement='top'
        shouldShowArrow={true}
        target={
          <NavTab
            className={isNested ? 'cu-nav-nested-item' : ''}
            isActive={isActive}
            isDisabled={isDisabled}
          >
            <>
              <StyledAnchor
                isDisabled={isDisabled}
                onClick={() => {
                  onClick?.(isActive);
                }}
                state={state}
                stateParams={stateParams}
              >
                {linkIcon && <div className='cu-nav-link-icon'>{linkIcon}</div>}
                <div className='cu-nav-link'>{text}</div>
                {linkBadge && (
                  <div className='cu-nav-link-badge'>{linkBadge}</div>
                )}
              </StyledAnchor>
              {linkAction && (
                <div className='cu-nav-link-action'>{linkAction}</div>
              )}
            </>
          </NavTab>
        }
      />
    </Container>
  );
};

const Container = withTracking(styled.div``);

const StyledAnchor = styled(RouteAnchor).attrs({
  className: 'cu-nav-link-wrapper',
})<{ isDisabled: boolean }>(
  ({ isDisabled }) => `
  /**
   * The cu-nav-tab styling disables pointer events to disallow clicks on disabled tabs,
   * but this in turn disallows popovers (i.e. tooltips) from appearing on hover when
   * they are added to a disabled nav item.
   * 
   * To mimic the appropriate functionality, we enable pointer events but force the cursor
   * back to default when the nav item is disabled.
   */
  pointer-events: all !important;
  cursor: ${isDisabled ? 'default' : 'pointer'} !important;

  /**
   * Don't show an underline decoration on anything except the link text.
   *
   * This selector is unnecessarily complicated because we're still bound to
   * unison's css styles. Since we've migrated all sidebars into frontend there's
   * nothing blocking us from dropping all of the unison classNames entirely and
   * relying on styled components instead. In the future, rip out the unison classes
   * and do all of the styling in this file.
   */
  &:hover {
    text-decoration: none !important;
  }
`,
);

const PageItemPopover = styled.div`
  padding: 8px;
`;

export default SidebarPageItem;
